/**
 * SENTRY INTEGRATION - API Error Tracking
 *
 * Changes made for Sentry:
 * - Import addSentryBreadcrumb, captureException, clearSentryUser from sentry.js
 * - Add breadcrumb for each API request (helps trace user actions before error)
 * - Capture server errors (500+) and send to Sentry with request details
 * - Clear Sentry user context on 401 (unauthorized) before redirect
 *
 * How it works in Sentry process:
 * - Breadcrumbs: Each API call is logged, so when an error occurs, Sentry shows
 *   the sequence of API calls that led to the error
 * - Server errors: 500+ errors are automatically reported with URL, method, and response
 * - 401 handling: Clears user context before logout to prevent wrong user attribution
 */
import axios from "axios";
import {showError, normalizeBlobError} from "helpers/notifications.helper";
import {getStorage, setSessionStorage} from "helpers/storage";
import {errorException} from "constants/error.consts";
import {SESSION_ENDED, TOKEN} from "constants/auth.const";
import {clearSentryUser} from "../sentry";

let baseURL = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({baseURL});

let sessionEnding = false;

// the session ended (revoked, expired or logged out elsewhere): out at once, the start page says why.
// Several requests fail together, only the first one redirects.
const endSession = () => {
  if (sessionEnding) return;
  sessionEnding = true;
  clearSentryUser();
  localStorage.clear();
  setSessionStorage(SESSION_ENDED, "1");
  window.location.replace("/");
};

axiosInstance.interceptors.request.use((config) => {
  const Authorization = `jwt ${getStorage("token")}`;
  const apikey = process.env.REACT_APP_API_KEY;
  config.headers = {Authorization, apikey};
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    error = await normalizeBlobError(error);
    const suppressErrors = error.config?.suppressErrors;

    if (!error.response && error.message === "Network Error") {
      if (!suppressErrors) showError("Network Error");
      return Promise.reject(error);
    }

    if (error.response) {
      const {status, data} = error.response;

      // a 401 with a message (e.g. a refused login) stays for 4 s so the message can be read;
      // logout handles its own 401
      if (
        status === 401 &&
        !data?.error &&
        getStorage(TOKEN) &&
        !error.config?.isLogout
      ) {
        endSession();
      } else if (status === 401) {
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/";
        }, 4000);
      }

      if (data?.error && !errorException.includes(data.error) && !suppressErrors)
        showError(data.error);
    } else if (!suppressErrors) showError("Unknown error");

    return Promise.reject(error);
  }
);

export default axiosInstance;
